<template>
  <div v-bind:class="signupFlipped ? 'flip-container flipped': 'flip-container'">
    <div class="flipper">
        <div class="front">
            <slot name="front"></slot>
            <v-card  elevation=6 class="signup-card">
              <v-card-title id="front-title" :style="cssVars"> {{ cardTitle }}</v-card-title>
              <v-card-text> Enter your mobile phone number,<br>we'll send you a one-time text. </v-card-text>
              <v-card-text>
                <v-text-field
                  id='phone-input'
                  ref='phoneInput'
                  v-model="phone"
                  placeholder="(***)-***-****"
                  label="Mobile Phone Number"
                  :color="colorFrontMain"
                  outlined
                  v-mask="phoneMask"
                  :rules="[rules.required, rules.phone]"
                  type="tel"
                  prepend-inner-icon="mdi-cellphone-iphone"
                  :loading='localLoading'
                >
                  <template v-slot:append-outer>
                    <v-btn id="submit-phone" :style="cssVars" class="appended-icon" icon rounded dark @click="submitPhone">
                      <v-icon> mdi-chevron-right </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-card-text>
              <div id="sign-in-promo">
                <v-divider />
                <v-card-actions>
                  <v-layout row >
                    <v-flex>
                      <v-btn text rounded :style="cssVars" class="front-btn font-weight-regular" @click="$router.push('/authentication/email/' + newUser)">
                        {{newUser ? 'Email Sign-up' : 'Email Sign-in'}}
                      </v-btn>
                    </v-flex>
                    <v-flex>
                      <v-btn text rounded :style="cssVars" class="front-btn font-weight-regular" @click="$router.push('/authentication/phone/' + !newUser)" >
                        {{newUser ? 'Returning User' : 'Create Account'}}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-card-actions>
              </div>
            </v-card>
        </div>
        <div class="back">
            <slot name="back"></slot>
            <v-card elevation=6 class="signup-card">
              <v-card-title id="back-title" :style="cssVars"> SMS Security Code </v-card-title>
              <v-card-text> We texted you 6-digit verification code,<br>please enter it below. </v-card-text>
              <v-card-text>
                <v-text-field
                  id='sms-input'
                  ref='smsInput'
                  v-model="smsCode"
                  placeholder="000000"
                  label="SMS Verification Code"
                  :color="colorBackMain"
                  outlined
                  v-mask="smsMask"
                  :rules="[rules.required, rules.code]"
                  type="num"
                  prepend-inner-icon="mdi-security"
                  :loading='localLoading'
                >
                  <template v-slot:append-outer>
                    <v-btn id="submit-sms" :style="cssVars" class="appended-icon" icon rounded dark @click="submitSMS">
                      <v-icon> mdi-chevron-right </v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-card-text>
              <div id="sign-in-promo">
                <v-divider />
                <v-card-actions>
                  <v-layout row >
                    <v-flex>
                      <v-btn text rounded :style="cssVars" class="back-btn font-weight-regular" @click="resetPhone">
                        Back
                      </v-btn>
                    </v-flex>
                    <v-flex>
                      <v-btn id='resend-btn' text rounded :style="cssVars" class="back-btn font-weight-regular" :disabled="!smsCodeResend" @click="resendSMSCode">
                        {{ smsCodeResend ? 'Resend Code': smsCodeCountdown + ' - Resend' }}
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-card-actions>
              </div>
            </v-card>
        </div>
    </div>
  </div>
</template>

<script>

import {
  mapGetters
} from 'vuex'

import { mask } from 'vue-the-mask'
import { unmaskPhone } from '@/utils/general'
import { firebase } from '@/utils/firebase'

export default {
  name: 'authCard',
  directives: {
    mask,
  },
  props: {
    titleFront: String,
    newUser: Boolean,
    colorFrontMain: String,
    colorFrontLight: String,
    colorBackMain: String,
    colorBackLight: String, 
    onSuccessPath: String, //Vuex path to push to if successful
  },
  data () {
      return {

          signupFlipped: false,
          localLoading: false,

          phone: null,
          phoneMask: '(###)###-####',

          smsCode: null,
          smsMask: '######',

          smsCodeIntervalObj: null,
          smsCodeCountdown: 0,
          smsCodeResend: false,

          rules: {
            required: value => !!value || 'Required.',
            phone: value => (value != null ? String(value).unmaskPhone().length == 10 : false) || 'Enter 10-digit US Phone Number.',
            email: value => {
              const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
              return pattern.test(value) || 'Invalid e-mail.'
            },
            code: value => (value != null ? value.length == 6 : false) || 'Must be a 6-digit code.',
          }
      }
  },
  mounted() {
    this.makePhoneRecaptcha()
  },
  computed: {
    ...mapGetters({
        debug: 'getDebug',
        loading: 'getLoading',
        isLogged: 'getIsLogged'
    }),
    cardTitle: function () {
      return this.newUser ? 'Create Account' : 'Welcome Back!'
    },
    cssVars() {
      return {
        '--color-front-main': this.colorFrontMain,
        '--color-front-light': this.colorFrontLight,
        '--color-back-main': this.colorBackMain,
        '--color-back-light': this.colorBackLight,
      }
    }
  },
  methods: {
    submitPhone: async function() {
      this.localLoading = true
      const unmasked = String(this.phone).unmaskPhone()
      if(this.$refs.phoneInput.validate(true)) {
        let self = this
        if(this.debug) console.log(`User submitted phone number: ${this.phone}`)
        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.NONE) // Set Vendor Persistance to Local
          .then(() => {
            return firebase.auth().signInWithPhoneNumber('+1' + (unmasked.toString()), window.recaptchaVerifier)
          })
          .then(function (confirmationResult) {
            if(self.debug) console.log(confirmationResult)
            // SMS sent. Prompt user to type the code from the message, then sign the
            // user in with confirmationResult.confirm(code).
            window.confirmationResult = confirmationResult

            // Block Resending SMS Code
            self.smsCodeResend = false
            let timerSeconds = 30;
            self.buttonTimer(timerSeconds);
            
            // Clear SMS Code Field
            self.smsCode = null

            try {
              self.makeSMSRecaptcha(false) // Reset Recaptcha
            } catch (err) {
              if(self.debug) console.error(err)
              self.makeSMSRecaptcha(true) // Reset Recaptcha
            }
            
            
            // Flip SignUp Card 
            self.signupFlipped = true
            // self.confirmPhone = false
            // self.nextField = true
            // self.$store.commit('SET_NOTIFICATION_TEXT', "Check your text messages for a security code.") 
            // self.$store.commit('SET_NOTIFICATION_TIMEOUT', 5000)
            // self.$store.commit('SET_NOTIFICATION')
          }).catch(function (error) {
            if(self.debug) console.error(error)
            if(self.signupFlipped) self.makeSMSRecaptcha(true)
            else self.makePhoneRecaptcha(true)  
          })
        // this.$refs.smsField.focus()
        }
        else {
            if(this.debug) console.error('Invalid Phone Number:', this.phone)
            // this.$store.commit('SET_NOTIFICATION_TEXT', "Full-10 digit US phone number is required. Please try again.") 
            // this.$store.commit('SET_NOTIFICATION_TIMEOUT', 5000)
            // this.$store.commit('SET_NOTIFICATION')
        }
       this.localLoading = false

    },
    resetPhone: function () {
      this.signupFlipped = false
      this.smsCodeResend = true
      this.smsCode = null
      if(this.smsCodeIntervalObj) clearInterval(this.smsCodeIntervalObj) // Kill SMS Resend timer (if running)
      this.makePhoneRecaptcha(true) // Reset Recaptcha
    },
    resendSMSCode: function () {
      this.submitPhone()
    },
    submitSMS: async function() {
      this.localLoading = true
      let self = this
     if(this.$refs.smsInput.validate(true)) {
      await window.confirmationResult.confirm(this.smsCode.toString())
        .then((result) => {
          if (this.debug) console.log('Successful Phone Sign In')
          if(this.onSuccessPath) {
            self.$router.push(self.onSuccessPath)
          }
          
          this.smsCodeResend = true
        
        })
        .catch((err) => {
          if(this.debug) console.error(`Error in SMS confirmation: ${err}`)
          // this.resetPhone()
        })
     }
      this.localLoading = false
    },
    makePhoneRecaptcha: function (reset=false) {
      let self = this
      this.localLoading = true
      if(this.debug) console.log('Reseting Phone Submit Button Recaptcha')
      setTimeout(() => {
        if(!reset || !window.recaptchaVerifier) {
          window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('submit-phone', {
            size: 'invisible',
            callback: () => {
                if (this.debug) console.log('User submitted reCAPTCHA')
            },
            'expired-callback': function () {
            // Response expired. Ask user to solve reCAPTCHA again.
            // ...
            }
          })
          window.recaptchaVerifier.render().then((widgetId) => {
            window.recaptchaWidgetId = widgetId
            if (self.debug) console.log('Recaptcha Widget ID: ', widgetId)
              
          })
        } else {
          window['recaptchaVerifier'].render().then((widgetId) => {
            grecaptcha.reset(widgetId)
            if (self.debug) console.log('Recaptcha Widget ID: ', widgetId)
            
          })
        }
      }, 1000)
      this.localLoading = false
    },
    makeSMSRecaptcha: function (reset=false) {
      let self = this
      this.localLoading = true
      if(this.debug) console.log('Reseting SMS Resend Button Recaptcha')
      setTimeout(() => {
        if(!reset || !window.recaptchaVerifier) {
          window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('resend-btn', {
            size: 'invisible',
            callback: () => {
                if (this.debug) console.log('User submitted reCAPTCHA')
            },
            'expired-callback': function () {
            // Response expired. Ask user to solve reCAPTCHA again.
            // ...
            }
          })
          window.recaptchaVerifier.render().then((widgetId) => {
            window.recaptchaWidgetId = widgetId
            if (self.debug) console.log('Recaptcha Widget ID: ', widgetId)
              
          })
        } else {
          window['recaptchaVerifier'].render().then((widgetId) => {
            grecaptcha.reset()
            if (self.debug) console.log('Recaptcha Widget ID: ', widgetId)
            
          }).catch((err) => {throw(new Error(err))})
        }
      }, 1000)
      this.localLoading = false
    },
    buttonTimer: function(duration) {
      let self = this
      var start = Date.now(),
          diff,
          minutes,
          seconds;
      function timer() {
          // get the number of seconds that have elapsed since 
          // startTimer() was called
          diff = duration - (((Date.now() - start) / 1000) | 0);

          // does the same job as parseInt truncates the float
          minutes = (diff / 60) | 0;
          seconds = (diff % 60) | 0;

          // minutes = minutes < 10 ? "0" + minutes : minutes;
          // seconds = seconds < 10 ? "0" + seconds : seconds;

          // display.textContent = minutes + ":" + seconds;
          seconds += minutes * 60; 
          self.smsCodeCountdown = seconds

          if (seconds <= 0){
            clearInterval(self.smsCodeIntervalObj)
            self.makeSMSRecaptcha(true)
            self.smsCodeResend = true
          } 
          // else if (diff <= 0) {
          //   // add one second so that the count down starts at the full duration
          //   // example 05:00 not 04:59
          //   start = Date.now() + 1000;
          // }
      };
      // we don't want to wait a full second before the timer starts
      timer();
      if(this.smsCodeIntervalObj) clearInterval(this.smsCodeIntervalObj)
      this.smsCodeIntervalObj = setInterval(timer, 1000);
    }
  } 
}
</script>


<style scoped>

.signup-card {
  height: auto;
  max-height: 300px;
  width: 100%;
  max-width: 300px;
  border-radius: 15px;
  padding: 2px;
}

#phone-input {
  margin-top: 10px;
}

#submit-phone {
  color: var(--color-front-main) 
}

#sms-input {
  margin-top: 10px;
}

#submit-sms {
  color: var(--color-back-main) 
}

.appended-icon {
  position: relative;
  left: 50%;
  margin-right: 50%;
  transform: translate(-50%, -15%);
}

#front-title {
  margin-bottom: 10px;
  color: var(--color-front-main) 
}

.front-btn {
  color: var(--color-front-light)  
}

#back-title {
  margin-bottom: 10px;
  color: var(--color-back-main) 
}

.back-btn {
  color: var(--color-back-light)  
}

</style>

<style type='text/css' scoped>
/* Card flip animation */

i.frontFlipBtn,
i.backFlipBtn {
    position:absolute; 
    right: 20px; 
    top: 20px;
    color:#FFFFFF;
}
i.backFlipBtn {
    -webkit-transform: rotateY(-180deg);
    -moz-transform: rotateY(-180deg);
    -o-transform: rotateY(-180deg);
    -ms-transform: rotateY(-180deg);
    transform: rotateY(-180deg);
}
.flip-container {
  -webkit-perspective: 1000;
  -moz-perspective: 1000;
  -o-perspective: 1000;
  perspective: 1000;
}
.flip-container {
  min-height: 120px;
}
.flipper {
  -moz-transform: perspective(1000px);
  -moz-transform-style: preserve-3d;
  position: relative;
}
.front,
.back {
  -webkit-backface-visibility: hidden;
  -moz-backface-visibility: hidden;
  -o-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-transition: 0.6s;
  -webkit-transform-style: preserve-3d;
  -moz-transition: 0.6s;
  -moz-transform-style: preserve-3d;
  -o-transition: 0.6s;
  -o-transform-style: preserve-3d;
  -ms-transition: 0.6s;
  -ms-transform-style: preserve-3d;
  transition: 0.6s;
  transform-style: preserve-3d;
  top: 0;
  left: 0;
  width: 100%;
}
.back {
  -webkit-transform: rotateY(-180deg);
  -moz-transform: rotateY(-180deg);
  -o-transform: rotateY(-180deg);
  -ms-transform: rotateY(-180deg);
  transform: rotateY(-180deg);
  position: absolute;
}
.flip-container.flipped .back {
  -webkit-transform: rotateY(0deg);
  -moz-transform: rotateY(0deg);
  -o-transform: rotateY(0deg);
  -ms-transform: rotateY(0deg);
  transform: rotateY(0deg);
}
.flip-container.flipped .front {
  -webkit-transform: rotateY(180deg);
  -moz-transform: rotateY(180deg);
  -o-transform: rotateY(180deg);
  -ms-transform: rotateY(180deg);
  transform: rotateY(180deg);
}
.front {
  z-index: 2;
}
</style>